import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/analytics";

import { getFunctions, httpsCallable } from "firebase/functions";

import config from "../firebaseConfig";
import firebase from "firebase/compat/app";

firebase.initializeApp(config);
export const auth = firebase.auth();
export const db = firebase.firestore();
export const storage = firebase.storage();
export const analytics = firebase.analytics();
const functions = getFunctions();

export const CLUB_ID = "cola-stl";

export const logEvent = (eventName, eventParams) => {
  analytics.logEvent(eventName, eventParams);
};

export const updateProfile = async (member, updates) => {
  try {
    const memberRef = db.collection(`clubs/${CLUB_ID}/members`).doc(member.id);
    await memberRef.update({
      ...updates,
      lastupdate: firebase.firestore.Timestamp.fromDate(new Date()),
    });
    const updatedMember = await memberRef.get();
    return updatedMember.data();
  } catch (error) {
    console.error({ error });
    return { error: true, ...error };
  }
};

export const getMemberOrders = async (memberId) => {
  try {
    const orders = await db
      .collection(`clubs/${CLUB_ID}/orders`)
      .where("memberId", "==", memberId)
      .get();
    const orderData = orders.docs
      .map((order) => ({
        id: order.id,
        ...order.data(),
      }))
      .sort((a, b) => {
        return a.createdate < b.createdate ? 1 : -1;
      });
    return orderData;
  } catch (error) {
    console.error({ error });
    return [];
  }
};

export const getOrCreateMember = async (user) => {
  const memberRef = db.collection(`clubs/${CLUB_ID}/members`).doc(user.uid);

  const memberSnapshot = await memberRef.get();
  if (!memberSnapshot.exists) {
    await memberRef.set({
      id: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      phoneNumber: user.phoneNumber,
      emailVerified: user.emailVerified,
      emailOptIn: false,
      waiverSigned: false,
      lastLogin: firebase.firestore.Timestamp.fromDate(new Date()),
      createdate: firebase.firestore.Timestamp.fromDate(new Date()),
      lastupdate: firebase.firestore.Timestamp.fromDate(new Date()),
    });
  } else {
    await memberRef.update({
      lastLogin: firebase.firestore.Timestamp.fromDate(new Date()),
    });
  }

  return memberRef;
};

// Events and Tickets
const createInstanceIdentifier = (instance) => {
  const startDate = new Date(instance.startDate.seconds * 1000);
  return `${startDate.getFullYear()}-${
    startDate.getMonth() + 1
  }-${startDate.getDate()}-${startDate.getHours()}-${startDate.getMinutes()}`;
};

const generateTicketSKU = (ticket, instance) => {
  const instanceIdentifier = createInstanceIdentifier(instance);
  return `${ticket.id}-${instanceIdentifier}`;
};

export const generateTicketsForInstance = (event, instance) => {
  if (!instance) return [];

  return (
    event.tickets?.map((ticket) => ({
      ...ticket,
      sku: generateTicketSKU(ticket, instance),
      eventId: event.id,
      eventName: event.name,
      startDate: instance.startDate,
      endDate: instance.endDate,
      eventInstanceId: instance.id,
    })) || []
  );
};

/** EVENTS */
export const getEvent = async (eventSlug) => {
  try {
    const eventRef = db.collection(`clubs/${CLUB_ID}/events`).doc(eventSlug);
    const eventSnapshot = await eventRef.get();

    if (!eventSnapshot.exists) {
      console.error(`Event with slug '${eventSlug}' not found.`);
      return { error: true, message: "Event not found" };
    }

    const eventData = eventSnapshot.data();
    const now = firebase.firestore.Timestamp.fromDate(new Date());

    const instancesSnapshot = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .where("eventId", "==", eventSlug)
      .where("endDate", ">", now)
      .orderBy("endDate")
      .limit(5) // Get up to 5 upcoming instances
      .get();

    if (!instancesSnapshot.empty) {
      // Store all instances
      eventData.instances = instancesSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      // Set the first instance as default selected
      eventData.selectedInstance = eventData.instances[0];
      eventData.nextInstance = eventData.instances[0];
      eventData.startDate = eventData.selectedInstance.startDate;
      eventData.endDate = eventData.selectedInstance.endDate;
    }

    eventData.tickets =
      eventData.tickets?.map((ticket) => {
        const instanceDate = new Date(
          eventData.nextInstance.startDate.seconds * 1000
        );
        const formattedDateTime = instanceDate.toLocaleString([], {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });

        return {
          ...ticket,
          sku: generateTicketSKU(ticket, eventData.nextInstance),
          eventId: eventData.id,
          eventName: eventData.name,
          instanceDateTime: formattedDateTime, // Human-readable date/time
          startDate: eventData.nextInstance?.startDate,
          endDate: eventData.nextInstance?.endDate,
          eventInstanceId: eventData.nextInstance?.id,
        };
      }) || [];

    return eventData;
  } catch (error) {
    console.error({ error });
    return { error: true, ...error };
  }
};

export const getEvents = async () => {
  try {
    const eventsRef = db.collection(`clubs/${CLUB_ID}/events`);
    const events = await eventsRef.get();
    const now = firebase.firestore.Timestamp.fromDate(new Date());

    const eventData = await Promise.all(
      events.docs.map(async (event) => {
        const eventId = event.id;
        const eventInfo = event.data();

        // Fetch the next instance for this event
        const instancesSnapshot = await db
          .collection(`clubs/${CLUB_ID}/eventInstances`)
          .where("eventId", "==", eventId)
          .where("endDate", ">", now)
          .orderBy("endDate")
          .limit(1)
          .get();

        if (!instancesSnapshot.empty) {
          const nextInstance = instancesSnapshot.docs[0].data();
          return {
            id: eventId,
            ...eventInfo,
            nextInstance: {
              ...nextInstance,
              id: instancesSnapshot.docs[0].id,
            },
          };
        }
        return null;
      })
    );

    // Filter out events with no future instances
    const filteredEvents = eventData.filter((event) => event !== null);

    // Sort by the next future instance start date
    const sortedEvents = filteredEvents.sort((a, b) => {
      return a.nextInstance.startDate - b.nextInstance.startDate;
    });

    return sortedEvents;
  } catch (error) {
    console.error({ error });
    return [];
  }
};

export const getEventInstancesForMonth = async (year, month) => {
  try {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999); // Last day of the month

    const instancesSnapshot = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .where("startDate", ">=", firebase.firestore.Timestamp.fromDate(startDate))
      .where("startDate", "<=", firebase.firestore.Timestamp.fromDate(endDate))
      .orderBy("startDate")
      .get();

    const eventIds = instancesSnapshot.docs.map((doc) => doc.data().eventId);
    const eventsSnapshot = await db
      .collection(`clubs/${CLUB_ID}/events`)
      .where(firebase.firestore.FieldPath.documentId(), "in", eventIds)
      .get();

    const eventsMap = eventsSnapshot.docs.reduce((map, eventDoc) => {
      map[eventDoc.id] = eventDoc.data();
      return map;
    }, {});

    const eventInstances = instancesSnapshot.docs.map((doc) => {
      const instanceData = { ...doc.data(), id: doc.id };
      const eventData = eventsMap[instanceData.eventId];
      if (eventData) {
        return {
          ...instanceData,
          event: eventData,
        };
      }
      return instanceData; // Return instance data even if event is not found
    });
    return eventInstances;
  } catch (error) {
    console.error("Error fetching event instances for month:", error);
    return [];
  }
};

export const getUpcomingEvents = async (limit = 5, withinDays = 14) => {
  try {
    const now = firebase.firestore.Timestamp.fromDate(new Date());
    const cutoff = firebase.firestore.Timestamp.fromDate(
      new Date(Date.now() + withinDays * 24 * 60 * 60 * 1000)
    );

    const instancesSnapshot = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .where("startDate", ">=", now)
      .where("startDate", "<=", cutoff)
      .orderBy("startDate", "asc")
      .limit(limit * 2) // fetch extra to account for deduplication
      .get();

    if (instancesSnapshot.empty) return [];

    // Group by eventId, keeping only the earliest instance per event
    const earliestByEvent = {};
    instancesSnapshot.docs.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      if (!earliestByEvent[data.eventId]) {
        earliestByEvent[data.eventId] = data;
      }
    });

    const instanceEntries = Object.values(earliestByEvent).slice(0, limit);
    const eventIds = instanceEntries.map((i) => i.eventId);

    // Firestore 'in' queries support max 10 items, which is fine for limit=5
    const eventsSnapshot = await db
      .collection(`clubs/${CLUB_ID}/events`)
      .where(firebase.firestore.FieldPath.documentId(), "in", eventIds)
      .get();

    const eventsMap = {};
    eventsSnapshot.docs.forEach((doc) => {
      eventsMap[doc.id] = { id: doc.id, ...doc.data() };
    });

    return instanceEntries
      .map((instance) => {
        const event = eventsMap[instance.eventId];
        if (!event) return null;
        return {
          ...event,
          nextInstance: instance,
        };
      })
      .filter(Boolean);
  } catch (error) {
    console.error("Error fetching upcoming events:", error);
    return [];
  }
};

// Club Config
export const getDiscount = async (code) => {
  if (!code) {
    return { error: true, message: "Invalid Discount Code" };
  }
  const cleanCode = code.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

  try {
    const discounts = await db
      .collection(`clubs/${CLUB_ID}/config/v1/discounts`)
      .where("code", "==", cleanCode)
      .get();
    const discount = discounts.docs[0];
    if (!discount.exists) {
      return { error: true, message: "Invalid Discount Code" };
    }
    return discount.data();
  } catch (error) {
    console.error({ error });
    return { error: true, ...error };
  }
};

export const getClubInfo = async () => {
  try {
    const clubCollection = db.collection(`clubs/${CLUB_ID}/config/v1/info`);
    const address = await clubCollection.doc("address").get();
    return {
      address: address.data(),
    };
  } catch (error) {
    console.error({ error });
  }
};

export const getClubProducts = async () => {
  try {
    const productRef = db.collection(`clubs/${CLUB_ID}/config/v1/products`);
    const products = await productRef.get();
    const productData = products.docs.reduce((acc, product) => {
      const { category } = product.data();
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({
        id: product.id,
        ...product.data(),
      });
      return acc;
    }, {});
    return productData;
  } catch (error) {
    console.error({ error });
    return {};
  }
};

export const getClubPassTemplates = async () => {
  try {
    const templateRef = db.collection(
      `clubs/${CLUB_ID}/config/v1/passtemplates`
    );
    const templates = await templateRef.get();
    const templateData = templates.docs
      .map((template) => ({
        id: template.id,
        ...template.data(),
      }))
      .sort((a, b) => {
        return a.order > b.order ? 1 : -1;
      });
    return templateData;
  } catch (error) {
    console.error({ error });
    return [];
  }
};

export const getOrder = async (orderId) => {
  try {
    const orderRef = db.collection(`clubs/${CLUB_ID}/orders`).doc(orderId);
    const order = await orderRef.get();
    return order.data();
  } catch (error) {
    console.error({ error });
    return {};
  }
};

export const getInvite = async (invitation) => {
  try {
    const inviteRef = db.collection(`clubs/${CLUB_ID}/invites`).doc(invitation);
    const invite = (await inviteRef.get()).data();
    return invite;
  } catch (error) {
    console.error({ error });
    return { error: true, message: "Invalid Invitation" };
  }
};

const validateEmail = (email) => {
  return !!email && email.includes("@") && email.length > 5;
};

export const claimPassByLink = async (linkId) => {
  try {
    const claimed = await httpsCallable(
      functions,
      "claimPassByLink"
    )({
      linkId,
      clubId: CLUB_ID,
    });
    return claimed;
  } catch (error) {
    console.error({ error });
    return { error: true, message: error.message };
  }
};

export const getSharedPasses = async (linkId) => {
  try {
    const sharedPasses = await httpsCallable(
      functions,
      "getSharedPasses"
    )({
      linkId,
      clubId: CLUB_ID,
    });
    return sharedPasses;
  } catch (error) {
    console.error({ error });
    return { error: true, message: error.message };
  }
};

export const sharePassesWithLink = async (passes, message) => {
  if (passes.length === 0) {
    return {
      error: true,
      message: "Please select at least one pass to share.",
    };
  }

  try {
    const shared = await httpsCallable(
      functions,
      "sharePassesWithLink"
    )({
      passes,
      message,
      clubId: CLUB_ID,
    });
    return shared;
  } catch (error) {
    console.error({ error });
    return { error: true, message: error.message };
  }
};

export const sharePass = async (passId, email) => {
  if (!validateEmail(email)) {
    return { error: true, message: "Please enter a valid email address" };
  }

  try {
    const shared = await httpsCallable(
      functions,
      "sharePass"
    )({
      passId,
      email,
      clubId: CLUB_ID,
    });
    return shared;
  } catch (error) {
    console.error({ error });
    return { error: true, message: error.message };
  }
};
export const claimPass = async (inviteId) => {
  try {
    const claimed = await httpsCallable(
      functions,
      "claimPass"
    )({
      inviteId,
      clubId: CLUB_ID,
    });
    return claimed;
  } catch (error) {
    console.error({ error });
    return { error: true, message: error.message };
  }
};

export const checkin = async (passId) => {
  try {
    const checkedIn = await httpsCallable(
      functions,
      "createCheckin"
    )({
      passId,
      clubId: CLUB_ID,
    });
    return checkedIn;
  } catch (error) {
    console.error({ error });
    return { error: true, ...error };
  }
};

export const createBudPass = async () => {
  const localStorageKey = "budPassCreation";
  try {
    // Check local storage for a cached response
    const cachedData = localStorage.getItem(localStorageKey);
    if (cachedData) {
      const { created, timestamp } = JSON.parse(cachedData);

      // Check if the cached response is from today
      const cachedDate = new Date(timestamp);
      const currentDate = new Date();
      if (cachedDate.toDateString() === currentDate.toDateString()) {
        return created; // Return cached response if it's from today
      }
    }

    // Create a new bud pass if no valid cached response
    const created = await httpsCallable(
      functions,
      "createBudPass"
    )({
      clubId: CLUB_ID,
    });

    // Cache the new response
    const cacheData = {
      created,
      timestamp: new Date().getTime(),
    };
    localStorage.setItem(localStorageKey, JSON.stringify(cacheData));

    return created;
  } catch (error) {
    console.error({ error });
    return { error: true, ...error };
  }
};

export const getFunction = (functionName) => {
  return httpsCallable(functions, functionName);
};

export const signOut = () => {
  auth.signOut();
};

export const centsToCurrency = (cents) => {
  return (parseInt(cents, 10) / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

// Polls

export const fetchUnansweredQuestion = async () => {
  const userId = auth.currentUser?.uid;
  const answeredQuestions = await db
    .collection(`clubs/${CLUB_ID}/answers`)
    .where("userId", "==", userId)
    .get();

  const answeredQuestionIds = answeredQuestions.docs.map(
    (doc) => doc.data().questionId
  );

  const allQuestions = await db.collection(`clubs/${CLUB_ID}/questions`).get();
  const unansweredQuestion = allQuestions.docs.find(
    (doc) => !answeredQuestionIds.includes(doc.id)
  );

  return unansweredQuestion
    ? { id: unansweredQuestion.id, ...unansweredQuestion.data() }
    : null;
};

export const submitAnswer = async (questionId, selectedOption) => {
  const userId = auth.currentUser?.uid;
  return await db.collection(`clubs/${CLUB_ID}/answers`).add({
    questionId,
    selectedOption,
    userId,
  });
};

/**
 * 
OPEN MIC FUNCTIONs
 */

export const getUpcomingOpenMicInstances = async (limit = 6) => {
  try {
    const now = new Date();
    const snapshot = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .where("eventId", "==", "pass-that-open-mic")
      .where("startDate", ">=", now)
      .orderBy("startDate", "asc")
      .limit(limit)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate.toDate(),
      endDate: doc.data().endDate.toDate(),
    }));
  } catch (error) {
    console.error("Error fetching upcoming open mic instances:", error);
    throw error;
  }
};

// Get past event instances for Pass That Open Mic
export const getPastOpenMicInstances = async (limit = 3) => {
  try {
    const now = new Date();
    const snapshot = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .where("eventId", "==", "pass-that-open-mic")
      .where("startDate", "<", now)
      .orderBy("startDate", "desc")
      .limit(limit)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate.toDate(),
      endDate: doc.data().endDate.toDate(),
    }));
  } catch (error) {
    console.error("Error fetching past open mic instances:", error);
    throw error;
  }
};

// Get a specific event instance
export const getEventInstance = async (instanceId) => {
  try {
    const doc = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .doc(instanceId)
      .get();

    if (!doc.exists) {
      return null;
    }

    return {
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate.toDate(),
      endDate: doc.data().endDate.toDate(),
    };
  } catch (error) {
    console.error("Error fetching event instance:", error);
    throw error;
  }
};

// Get the next upcoming open mic instance
export const getNextOpenMicInstance = async () => {
  const instances = await getUpcomingOpenMicInstances(1);
  return instances.length > 0 ? instances[0] : null;
};

export const registerForOpenMic = async (
  name,
  stageName,
  email,
  instagram,
  facebook,
  tiktok,
  youtube,
  otherSocial,
  eventInstanceId = null
) => {
  try {
    // If no event instance ID is provided, get the next upcoming one
    let instanceId = eventInstanceId;
    if (!instanceId) {
      const nextInstance = await getNextOpenMicInstance();
      if (!nextInstance) {
        throw new Error("No upcoming open mic events found");
      }
      instanceId = nextInstance.id;
    }

    // Create the signup document
    const docRef = await db.collection("openMicSignups").add({
      name,
      stageName,
      email,
      instagram,
      facebook,
      tiktok,
      youtube,
      otherSocial,
      eventInstanceId: instanceId,
      selected: false,
      confirmed: false,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    return docRef.id;
  } catch (error) {
    console.error("Error registering for open mic:", error);
    throw error;
  }
};

export const getOpenMicSignups = async (eventInstanceId = null) => {
  try {
    let query = db.collection("openMicSignups");

    if (eventInstanceId) {
      query = query.where("eventInstanceId", "==", eventInstanceId);
    }

    const snapshot = await query.get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt, // This will be a Timestamp object
    }));
  } catch (error) {
    console.error("Error fetching open mic signups:", error);
    throw error;
  }
};

export const updatePerformerStatus = async (id, selected) => {
  try {
    await db.collection("openMicSignups").doc(id).update({
      selected,
      lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error("Error updating performer status:", error);
    throw error;
  }
};

export const removePerformer = async (id) => {
  await db.collection("openMicSignups").doc(id).delete();
};

export const sendPerformerEmails = async (emailData, eventInstance) => {
  try {
    // Include event instance details in the callable function
    const sendEmails = httpsCallable("sendPerformerEmails");
    const result = await sendEmails({
      ...emailData,
      eventInstance: {
        id: eventInstance.id,
        startDate: eventInstance.startDate.toISOString(),
        endDate: eventInstance.endDate.toISOString(),
      },
    });
    return result.data;
  } catch (error) {
    console.error("Error sending performer emails:", error);
    throw error;
  }
};

export const getPerformerStatus = async (performerId) => {
  try {
    const doc = await db.collection("openMicSignups").doc(performerId).get();

    if (!doc.exists) {
      return { exists: false };
    }

    const data = doc.data();
    return {
      exists: true,
      selected: data.selected || false,
      confirmed: data.confirmed || false,
      eventInstanceId: data.eventInstanceId || null,
      name: data.name,
      stageName: data.stageName,
      email: data.email,
    };
  } catch (error) {
    console.error("Error getting performer status:", error);
    throw error;
  }
};

export const confirmPerformance = async (performerId) => {
  try {
    // Generate a discount code (optional)
    const discountCode = `OPENMIC-${Math.random()
      .toString(36)
      .substr(2, 6)
      .toUpperCase()}`;

    // Update the performer document
    await db.collection("openMicSignups").doc(performerId).update({
      confirmed: true,
      confirmedAt: firebase.firestore.FieldValue.serverTimestamp(),
      discountCode: discountCode,
    });

    return { success: true, discountCode };
  } catch (error) {
    console.error("Error confirming performance:", error);
    throw error;
  }
};

/**
 * VENDOR FUNCTIONS
 */

export const registerVendor = async (values) => {
  try {
    const vendorRef = await db.collection(`clubs/${CLUB_ID}/vendors`).add({
      ...values,
      status: "pending",
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    return { success: true, vendorId: vendorRef.id };
  } catch (error) {
    console.error("Error registering vendor:", error);
    throw new Error("Failed to register vendor");
  }
};

export const getActiveEvent = async () => {
  const now = new Date();
  const startOfToday = new Date(now.setHours(0, 0, 0, 0));
  const endOfToday = new Date(now.setHours(23, 59, 59, 999));

  try {
    const instanceSnapshot = await db
      .collection(`clubs/${CLUB_ID}/eventInstances`)
      .where("startDate", ">=", startOfToday)
      .where("startDate", "<=", endOfToday)
      .limit(1) // Get the first event of the day
      .get();

    if (instanceSnapshot.empty) {
      return null;
    }

    const instance = instanceSnapshot.docs[0].data();
    const eventRef = db
      .collection(`clubs/${CLUB_ID}/events`)
      .doc(instance.eventId);

    const eventSnapshot = await eventRef.get();

    if (!eventSnapshot.exists) {
      return null;
    }

    return {
      ...eventSnapshot.data(),
      startDate: instance.startDate,
      endDate: instance.endDate,
      instance: {
        id: instanceSnapshot.docs[0].id,
        ...instance,
      },
    };
  } catch (error) {
    console.error("Error getting today's event:", error);
    throw error;
  }
};

export const getHostSchedule = async () => {
  try {
    const result = await httpsCallable(functions, "getHostSchedule")();
    return result;
  } catch (e) {
    console.error({ e });
    throw e;
  }
};
